import React from "react";

import SelectorBoxEntry from "./SelectorBoxEntry"

import "./Selector.scss";;

interface SelectorBoxProps<T> {
    group?: any;
    source: T[];
    value: T[];
    keyField: string;
    nameField?: string;
    displayField?: string;
    className?: string;
    inline?: boolean;
    stopAdd?: boolean;
    stopRemove?: boolean;
    readOnly?: boolean;
    allowCustomInput?: boolean;
    mandatory?: boolean;
    showName?: boolean;
    placeholder?: string;
    displayFunc?: (item: T) => string;
    onAdd?: (value: T) => void;
    onRemove?: (value: T) => void;
    onCreate?: (value: string) => void;
    onDelete?: () => void;
}

interface SelectorBoxState<T> {
    filterValue: string;
    addItemsVisible: boolean;
    visibleChildData: T[];
    addInput: React.RefObject<HTMLInputElement>;
}

class SelectorBox<T extends {}> extends React.Component<
    SelectorBoxProps<T>,
    SelectorBoxState<T>
> {
    state = {
        filterValue: "",
        addItemsVisible: false,
        visibleChildData: [],
        addInput: React.createRef<HTMLInputElement>(),
    };

    componentDidMount(): void {
        this.setVisibleItems();
    }

    componentDidUpdate(
        prevProps: Readonly<SelectorBoxProps<T>>,
        prevState: Readonly<SelectorBoxState<T>>,
        snapshot?: any,
    ): void {
        if (
            prevProps.source !== this.props.source ||
            prevProps.value !== this.props.value
        )
            this.setVisibleItems();
    }

    shiftFocus() {
        this.state.addInput.current?.focus();
    }

    showaddItems() {
        this.setState(() => ({ addItemsVisible: true }));
    }

    hideaddItems() {
        this.setState(() => ({ addItemsVisible: false }));
    }

    clickItem(item: any) {
        this.props.onAdd?.(item);
        this.state.addInput.current?.focus();
    }

    filterChanged(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState(() => ({ filterValue: event.target.value }));

        // Only filter when dropdown is visible
        if (!this.state.addItemsVisible) return;

        // Apply filter to all child data
        this.setVisibleItems();
    }

    evaluateFilter(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "Enter":
                // Add the currently selected item
                if (this.props.onCreate && this.state.filterValue.trim() !== "")
                    this.props.onCreate(this.state.filterValue);
                this.setState(() => ({ filterValue: "" }));
                this.setVisibleItems();
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    setVisibleItems() {
        this.setState(() => ({
            visibleChildData:
                this.props.source?.filter(
                    item =>
                        (this.state.filterValue.length === 0 ||
                            item[this.props.keyField].startsWith(
                                this.state.filterValue,
                            )) &&
                        !this.props.value.some(
                            value =>
                                value[this.props.keyField] ===
                                item[this.props.keyField],
                        ),
                ) ?? [],
        }));
    }

    render(): React.ReactNode {
        return (
            <div
                className={`selector-box ${this.props.className ?? ""}${
                    !this.props.stopAdd ? " open-add" : ""
                }${this.props.inline ? " inline-mode" : ""}`}>
                {this.props.onDelete && !this.props.readOnly && (
                    <span
                        className="selector-box-remove"
                        onClick={() => this.props.onDelete()}></span>
                )}

                {this.props.group &&
                    this.props.nameField &&
                    this.props.showName && (
                        <>
                            <input
                                type="text"
                                className="selector-box-name"
                                value={
                                    this.props.group[this.props.nameField] ?? ""
                                }
                                readOnly={this.props.readOnly}
                            />

                            <span className="print-only selector-box-name">
                                {this.props.group[this.props.nameField] ?? ""}
                            </span>
                        </>
                    )}

                <span
                    tabIndex={0}
                    className={`selector-box-entries${
                        this.props.mandatory ? " mandatory" : ""
                    }`}
                    onFocus={() => this.shiftFocus()}>
                    <span className="selector-box-entries-selected">
                        {this.props.value.map((item, index) => (
                            <SelectorBoxEntry
                                key={index}
                                value={item}
                                displayField={this.props.displayField}
                                displayFunc={this.props.displayFunc}
                                allowDelete={
                                    !this.props.readOnly &&
                                    !this.props.stopRemove
                                }
                                onDelete={value =>
                                    this.props.onRemove?.(value)
                                }></SelectorBoxEntry>
                        ))}
                    </span>

                    {!this.props.stopAdd && !this.props.readOnly && (
                        <input
                            ref={this.state.addInput}
                            className="selector-box-add"
                            placeholder={
                                this.props.placeholder ?? "Add item..."
                            }
                            value={this.state.filterValue}
                            onBlur={() => this.hideaddItems()}
                            onFocus={() => this.showaddItems()}
                            onChange={event => this.filterChanged(event)}
                            onKeyDown={event => this.evaluateFilter(event)}
                        />
                    )}

                    <span
                        className={`selector-box-add-list${
                            this.state.addItemsVisible ? " open" : ""
                        }`}>
                        {this.state.visibleChildData.map((item, index) => (
                            <span
                                key={index}
                                onClick={() => this.clickItem(item)}
                                data-index={index}>
                                {this.props.displayFunc?.(item) ??
                                    item[this.props.displayField ?? ""]}
                            </span>
                        ))}

                        {this.state.visibleChildData.length === 0 && (
                            <span className="no-results">No Results</span>
                        )}
                    </span>
                </span>
            </div>
        );
    }
}

export default SelectorBox;
